import { Component } from "@theme/component";
import { debounce, isClickedOutside, onAnimationEnd } from "@theme/utilities";

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ["dialog"];

  #isNonModalOpen = false;
  #outsideClickListenerTimeout = null;
  #ignoreOutsideClicksUntil = 0;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener("resize", this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener("resize", this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      const shouldUseNonModalDesktop =
        this.desktopMode === "non-modal" && window.innerWidth >= 750;

      if (shouldUseNonModalDesktop) {
        this.#isNonModalOpen = true;
        this.#ignoreOutsideClicksUntil = Date.now() + 300;
        dialog.show();
      } else {
        this.#isNonModalOpen = false;
        this.#ignoreOutsideClicksUntil = 0;
        document.body.style.width = "100%";
        document.body.style.position = "fixed";
        document.body.style.top = `-${scrollY}px`;
        dialog.showModal();
      }

      this.dispatchEvent(new DialogOpenEvent());

      document.addEventListener("keydown", this.#handleDocumentKeyDown, true);

      if (shouldUseNonModalDesktop) {
        this.#outsideClickListenerTimeout = window.setTimeout(() => {
          document.addEventListener("click", this.#handleDocumentClick, true);
          this.#outsideClickListenerTimeout = null;
        }, 0);
      } else {
        document.addEventListener("click", this.#handleDocumentClick, true);
      }
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    if (this.#outsideClickListenerTimeout !== null) {
      window.clearTimeout(this.#outsideClickListenerTimeout);
      this.#outsideClickListenerTimeout = null;
    }

    document.removeEventListener("click", this.#handleDocumentClick, true);
    document.removeEventListener("keydown", this.#handleDocumentKeyDown, true);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = "none";

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add("dialog-closing");
    dialog.style.animation = "";

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    if (!this.#isNonModalOpen) {
      document.body.style.width = "";
      document.body.style.position = "";
      document.body.style.top = "";
      window.scrollTo({ top: this.#previousScrollY, behavior: "instant" });
    }

    dialog.close();
    dialog.classList.remove("dialog-closing");
    this.#isNonModalOpen = false;

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleDocumentClick = (event) => {
    const { dialog } = this.refs;

    if (this.#isNonModalOpen && Date.now() < this.#ignoreOutsideClicksUntil) {
      return;
    }

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleDocumentKeyDown = (event) => {
    if (event.key !== "Escape") return;

    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute("dialog-active-min-width"));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute("dialog-active-max-width"));
  }

  get desktopMode() {
    return this.getAttribute("dialog-desktop-mode");
  }
}

if (!customElements.get("dialog-component"))
  customElements.define("dialog-component", DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = "dialog:open";
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = "dialog:close";
}

document.addEventListener(
  "toggle",
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute("scroll-lock")) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute("scroll-lock", "");
        } else {
          document.documentElement.removeAttribute("scroll-lock");
        }
      }
    }
  },
  { capture: true },
);
